import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
    Box,
    Button,
    IconButton,
    TextField,
    Autocomplete,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    Paper,
    Stack,
    Avatar,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Divider,
} from "@mui/material";
import {
    DatePicker,
    LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import { SESSION_TIME_RANGES, normalizeSession } from "../../../Utility/SessionSlotConfig";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Colors } from "../../../Styles/Colors";
import Loader from "../../../Components/Loader";

// Avatar color palette: light background + dark letter (same hue) for each avatar.
const AVATAR_COLOR_PALETTE = [
    { bg: "#E3F2FD", color: "#1565C0" },
    { bg: "#E8F5E9", color: "#2E7D32" },
    { bg: "#F3E5F5", color: "#6A1B9A" },
    { bg: "#FFEBEE", color: "#C62828" },
    { bg: "#E1F5FE", color: "#0277BD" },
    { bg: "#E8F5E9", color: "#1B5E20" },
    { bg: "#EDE7F6", color: "#4A148C" },
    { bg: "#FBE9E7", color: "#BF360C" },
    { bg: "#E0F7FA", color: "#006064" },
    { bg: "#C8E6C9", color: "#1B5E20" },
    { bg: "#BBDEFB", color: "#0D47A1" },
    { bg: "#FFE0B2", color: "#E65100" },
    { bg: "#E1BEE7", color: "#4A148C" },
    { bg: "#B2DFDB", color: "#00695C" },
    { bg: "#FFCCBC", color: "#BF360C" },
];

/** Returns { bg, color } for avatar from name + index. Same name+index always gets same color. */
function getAvatarColors(name, index = 0) {
    const str = typeof name === "string" ? name : "";
    const hash = str.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const idx = (Math.abs(hash) + (Number(index) || 0)) % AVATAR_COLOR_PALETTE.length;
    return AVATAR_COLOR_PALETTE[idx] || AVATAR_COLOR_PALETTE[0];
}

// Session time ranges from SessionSlotConfig (same as TreatmentSlotConfig): Morning 6-11 AM, Afternoon 12-4 PM, Evening 4-8 PM

const SchedulerView = ({
    initialSchedule = null,
    onBack = null,
    patientDetails = null,
    therapyDetails = null,
    treatmentDate = null,
    duration = null,
    instructions = null,
    no_of_therapist = null,
    treatment_ms_id = null,
    session: sessionProp = null,
}) => {
    // Get date from props or use initialSchedule
    const getInitialDate = () => {
        if (treatmentDate) {
            // Handle DD-MM-YYYY format from API or YYYY-MM-DD format
            if (treatmentDate.includes("-") && treatmentDate.split("-")[0].length === 2) {
                return dayjs(treatmentDate, "DD-MM-YYYY");
            }
            return dayjs(treatmentDate);
        }
        if (initialSchedule?.treatment_date) {
            // Handle DD-MM-YYYY format from API
            if (initialSchedule.treatment_date.includes("-") && initialSchedule.treatment_date.split("-")[0].length === 2) {
                return dayjs(initialSchedule.treatment_date, "DD-MM-YYYY");
            }
            return dayjs(initialSchedule.treatment_date);
        }
        return dayjs();
    };

    const [gridDate, setGridDate] = useState(getInitialDate());
    const [schedules, setSchedules] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [availableTherapists, setAvailableTherapists] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [selectedTherapists, setSelectedTherapists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [therapistLoading, setTherapistLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    // Filter states
    const [roomFilter, setRoomFilter] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    // Drag selection state
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const gridRef = useRef(null);
    
    // Simple ref to track last fetched selection
    const lastFetchedSlotsRef = useRef(null); // Store last fetched slots as string key
    const lastFetchedRoomIdRef = useRef(null); // Store last fetched room ID
    
    // Time slots configuration - 6 AM to 8 PM, 15 minutes each
    const startTime = dayjs().hour(6).minute(0);
    const endTime = dayjs().hour(20).minute(0);
    const slotDuration = 15; // minutes 
    const timeSlots = [];
    for (let time = startTime; time.isBefore(endTime); time = time.add(slotDuration, "minute")) {
        timeSlots.push(time);
    }

    const currentTime = dayjs();

    // Get patient and therapy details from props
    const patientInfo = patientDetails || initialSchedule;
    const therapyInfo = therapyDetails || initialSchedule;
    const treatmentDuration = duration || initialSchedule?.treatments?.reduce((acc, t) => acc + +t.duration, 0) || 0;
    const treatmentInstructions = instructions || initialSchedule?.instructions || initialSchedule?.instruction || "";
    const therapistCount = no_of_therapist || (initialSchedule?.treatments?.length ? Math.max(...initialSchedule.treatments.map(t => t.no_of_therapist || 0)) : 0) || 1;
    // Handle treatment_ms_id from props or initialSchedule
    const treatmentId = treatment_ms_id 
        ? (Array.isArray(treatment_ms_id) ? treatment_ms_id : [treatment_ms_id])
        : (initialSchedule?.treatments?.map((t) => t.treatment_ms_id) || []);

    // Fetch schedules for grid - structured like other components
    const fetchSchedules = useCallback(() => {
        if (!gridDate) return;
        setLoading(true);
        setErrorMsg("");
        const dateStr = gridDate.format("YYYY-MM-DD");
        RestAPIService.getAll(
            `${Serviceurls.AYURVEDA_SCHEDULER_LIST_VIEW}?date=${dateStr}`
        )
            .then((res) => {
                if (res?.data?.status === "success" && res.data.data && res.data.data.length > 0) {
                    // API returns array of rooms with nested schedules
                    // Flatten the structure: extract schedules and add room_id to each
                    const flattenedSchedules = [];
                    const roomsData = [];

                    res.data.data.forEach((room) => {
                        // Store room data
                        roomsData.push({
                            id: room.room_id,
                            room_id: room.room_id,
                            room_code: room.room_code,
                            room_name: room.room_name,
                            room_number: room.room_name,
                        });

                        // Flatten schedules and add room_id to each schedule
                        if (room.schedules && room.schedules.length > 0) {
                            room.schedules.forEach((schedule) => {
                                flattenedSchedules.push({
                                    ...schedule,
                                    therapy_room_id: room.room_id,
                                    room_id: room.room_id,
                                    treatment_time: schedule.start_time, // Map start_time to treatment_time
                                    // Keep end_time as is
                                    therapist_names: schedule.therapists
                                        ? schedule.therapists.map((t) => t.therapist_name)
                                        : [],
                                });
                            });
                        }
                    });

                    setSchedules(flattenedSchedules);
                    // Also update rooms from the API response
                    if (roomsData.length > 0) {
                        setRooms(roomsData);
                    }
                }
                
            })
            .catch((error) => {
                console.error("Error fetching schedules:", error);
                setSchedules([]);
                setRooms([]);
                setAvailableTherapists([]);
                setSelectedSlots([]);
                setSelectedTherapists([]);
                setSelectedRoom(null);
                lastFetchedSlotsRef.current = null;
                lastFetchedRoomIdRef.current = null;
                setErrorMsg("Failed to load schedules.");
            })
            .finally(() => setLoading(false));
    }, [gridDate]);

    // Helper function to create a key from slots and room
    const getSelectionKey = useCallback((slots, room) => {
        if (!slots || !slots.length || !room) return null;
        const slotsKey = slots.map(s => s.format("HH:mm:ss")).sort().join(",");
        return `${slotsKey}_${room.id}`;
    }, []);

    // Fetch available therapists - simple version
    const fetchAvailableTherapists = useCallback((slots, room) => {
        if (!slots || !slots.length || !room) {
            setAvailableTherapists([]);
            return;
        }

        setTherapistLoading(true);

        const firstSlot = slots[0];
        const lastSlot = slots[slots.length - 1];
        const startTime = firstSlot.format("HH:mm:ss");
        const endTime = lastSlot.add(slotDuration, "minute").format("HH:mm:ss");
        const dateStr = gridDate.format("YYYY-MM-DD");
        const url = `${Serviceurls.AYURVEDA_AVAILABLE_THERAPISTS}?treatment_date=${dateStr}&treatment_time=${startTime}&end_time=${endTime}&therapy_room_id=${room.id}&treatment_ms_id=${treatmentId.join(",") || ""}`;

        RestAPIService.getAll(url)
            .then((res) => {
                if (res?.data?.status === "success") {
                    setAvailableTherapists(res.data.data || []);
                    // Update last fetched refs
                    lastFetchedSlotsRef.current = getSelectionKey(slots, room);
                    lastFetchedRoomIdRef.current = room.id;
                } else {
                    setAvailableTherapists([]);
                }
            })
            .catch((error) => {
                console.error("Error fetching available therapists:", error);
                setAvailableTherapists([]);
            })
            .finally(() => {
                setTherapistLoading(false);
            });
    }, [gridDate, slotDuration, treatmentId, getSelectionKey]);

    // Fetch rooms - structured like other components
    const fetchRooms = useCallback(() => {
        RestAPIService.getAll(Serviceurls.AYURVEDA_THERAPY_ROOM_MASTER)
            .then((res) => {
                if (res?.data?.status === "success" && res.data.data && res.data.data.length > 0) {
                    setRooms(res.data.data);
                }
            })
            .catch((error) => {
                console.error("Error fetching rooms:", error);
            });
    }, []);

    // Helper function to parse 12-hour format time string to dayjs object
    const parseTimeString = useCallback((timeStr) => {
        if (!timeStr) return null;
        // Handle formats like "10:00 AM" or "10:00:00" or "HH:mm:ss"
        if (timeStr.includes("AM") || timeStr.includes("PM")) {
            // 12-hour format: "10:00 AM" or "11:00 PM"
            return dayjs(timeStr, "hh:mm A");
        } else if (timeStr.includes(":")) {
            // 24-hour format: "10:00:00" or "HH:mm:ss" or "HH:mm"
            const withSeconds = dayjs(timeStr, "HH:mm:ss", true);
            if (withSeconds.isValid()) return withSeconds;
            return dayjs(timeStr, "HH:mm", true);
        }
        return null;
    }, []);

    // When treatment_time is set, only allow that start time for duration minutes
    const fixedStartTime = useMemo(() => {
        const timeStr = initialSchedule?.treatment_time;
        if (!timeStr || !treatmentDuration) return null;
        return parseTimeString(timeStr);
    }, [initialSchedule?.treatment_time, treatmentDuration, parseTimeString]);

    const allowedSlotsForFixedTime = useMemo(() => {
        if (!fixedStartTime || !treatmentDuration) return null;
        const startMinutes = fixedStartTime.hour() * 60 + fixedStartTime.minute();
        const endMinutes = startMinutes + treatmentDuration;
        return timeSlots.filter((slot) => {
            const slotMinutes = slot.hour() * 60 + slot.minute();
            return slotMinutes >= startMinutes && slotMinutes < endMinutes;
        });
    }, [fixedStartTime, treatmentDuration, timeSlots]);

    const isSlotAllowedForFixedTime = useCallback((slotTime) => {
        if (!fixedStartTime) return true;
        if (!allowedSlotsForFixedTime || !allowedSlotsForFixedTime.length) return false;
        return allowedSlotsForFixedTime.some((s) => s.isSame(slotTime, "minute"));
    }, [fixedStartTime, allowedSlotsForFixedTime]);

    // Session (Morning/Afternoon/Evening) from props or initialSchedule - restrict slots to session time range (slot config timing)
    const currentSession = useMemo(
        () => normalizeSession(sessionProp || initialSchedule?.session || null),
        [sessionProp, initialSchedule?.session]
    );
    const isSlotWithinSession = useCallback((slotTime) => {
        if (!currentSession || !SESSION_TIME_RANGES[currentSession]) return true;
        const range = SESSION_TIME_RANGES[currentSession];
        const slotMinutes = slotTime.hour() * 60 + slotTime.minute();
        const startMinutes = range.start.hour * 60 + range.start.minute;
        const endMinutes = range.end.hour * 60 + range.end.minute;
        return slotMinutes >= startMinutes && slotMinutes < endMinutes;
    }, [currentSession]);

    // Get schedule for a specific room and slot time
    const getScheduleForSlot = useCallback((room, slotTime) => {
        return schedules.find((s) => {
            // Only show scheduled items (therapy_room_id or room_id is not null)
            const scheduleRoomId = s.therapy_room_id || s.room_id;
            if (!scheduleRoomId || scheduleRoomId !== room.id) return false;

            // Use start_time if treatment_time is not available
            const timeField = s.treatment_time || s.start_time;
            const scheduleStart = parseTimeString(timeField);
            const scheduleEnd = parseTimeString(s.end_time);

            if (!scheduleStart || !scheduleEnd) return false;

            // Set the date for comparison
            const scheduleStartDateTime = gridDate.hour(scheduleStart.hour()).minute(scheduleStart.minute());
            const scheduleEndDateTime = gridDate.hour(scheduleEnd.hour()).minute(scheduleEnd.minute());
            const slotDateTime = gridDate.hour(slotTime.hour()).minute(slotTime.minute());

            // Check if slot is within schedule time range
            return (
                (slotDateTime.isSame(scheduleStartDateTime) || slotDateTime.isAfter(scheduleStartDateTime)) &&
                slotDateTime.isBefore(scheduleEndDateTime)
            );
        });
    }, [schedules, gridDate, parseTimeString]);

    // 15-min buffer: slot 15 min before booking start or slot at booking end cannot be selected
    const BUFFER_MINUTES = 15;
    const isSlotInBookingBuffer = useCallback((room, slotTime) => {
        const scheduleRoomSchedules = schedules.filter((s) => {
            const rid = s.therapy_room_id || s.room_id;
            return rid && rid === room.id;
        });
        const slotDateTime = gridDate.hour(slotTime.hour()).minute(slotTime.minute());
        for (const s of scheduleRoomSchedules) {
            const scheduleStart = parseTimeString(s.treatment_time || s.start_time);
            const scheduleEnd = parseTimeString(s.end_time);
            if (!scheduleStart || !scheduleEnd) continue;
            const scheduleStartDt = gridDate.hour(scheduleStart.hour()).minute(scheduleStart.minute());
            const scheduleEndDt = gridDate.hour(scheduleEnd.hour()).minute(scheduleEnd.minute());
            // Slot 15 min before booking start (e.g. 7:45 when booking is 8:00-10:00)
            const bufferBeforeStart = scheduleStartDt.subtract(BUFFER_MINUTES, "minute");
            if (slotDateTime.isSame(bufferBeforeStart, "minute")) return true;
            // Slot at booking end (e.g. 10:00 when booking ends at 10:00)
            if (slotDateTime.isSame(scheduleEndDt, "minute")) return true;
        }
        return false;
    }, [schedules, gridDate, parseTimeString]);

    // Check if this slot is the start of a schedule (to determine if we should show the schedule data)
    const isScheduleStart = useCallback((room, slotTime, schedule) => {
        if (!schedule) return false;
        // Use start_time if treatment_time is not available
        const timeField = schedule.treatment_time || schedule.start_time;
        const scheduleStart = parseTimeString(timeField);
        if (!scheduleStart) return false;
        const scheduleStartDateTime = gridDate.hour(scheduleStart.hour()).minute(scheduleStart.minute());
        const slotDateTime = gridDate.hour(slotTime.hour()).minute(slotTime.minute());
        return scheduleStartDateTime.isSame(slotDateTime, "minute");
    }, [gridDate, parseTimeString]);

    // Calculate how many slots a schedule spans
    const getScheduleSpan = useCallback((schedule) => {
        if (!schedule) return 1;
        // Use start_time if treatment_time is not available
        const timeField = schedule.treatment_time || schedule.start_time;
        const scheduleStart = parseTimeString(timeField);
        const scheduleEnd = parseTimeString(schedule.end_time);

        if (!scheduleStart || !scheduleEnd) return 1;

        const durationMinutes = scheduleEnd.diff(scheduleStart, "minute");
        const span = Math.ceil(durationMinutes / slotDuration);
        return Math.max(1, span);
    }, [slotDuration, parseTimeString]);

    useEffect(() => {
        fetchSchedules();
        fetchRooms();
    }, [fetchSchedules, fetchRooms]);

    // Sync grid date from therapy date only when treatmentDate prop changes (e.g. initial load).
    // Do not overwrite gridDate when user changes date via picker/arrows.
    useEffect(() => {
        if (treatmentDate) {
            const parsed = treatmentDate.includes("-") && treatmentDate.split("-")[0].length === 2
                ? dayjs(treatmentDate, "DD-MM-YYYY")
                : dayjs(treatmentDate);
            if (parsed.isValid()) {
                setGridDate(parsed);
                setSelectedSlots([]);
                setSelectedTherapists([]);
                setSelectedRoom(null);
                lastFetchedSlotsRef.current = null;
                lastFetchedRoomIdRef.current = null;
            }
        }
    }, [treatmentDate]);

    // Handle slot selection - works with individual slots
    const handleSlotClick = useCallback((room, slotTime) => {
        const slotDateTime = gridDate.hour(slotTime.hour()).minute(slotTime.minute());
        if (slotDateTime.isBefore(currentTime)) {
            return;
        }

        const schedule = getScheduleForSlot(room, slotTime);
        if (schedule) {
            return; // Don't allow selection of occupied slots
        }

        if (isSlotInBookingBuffer(room, slotTime)) return; // 15-min buffer before/after booking

        if (currentSession && !isSlotWithinSession(slotTime)) return; // Only allow slots within session time range

        // When treatment_time is set, only allow the fixed start-time block
        if (fixedStartTime && allowedSlotsForFixedTime?.length) {
            if (!isSlotAllowedForFixedTime(slotTime)) return;
            setSelectedRoom(room);
            setSelectedSlots([...allowedSlotsForFixedTime]);
            setSelectedTherapists([]);
            setTimeout(() => {
                const newKey = getSelectionKey(allowedSlotsForFixedTime, room);
                if (newKey && newKey !== lastFetchedSlotsRef.current) {
                    fetchAvailableTherapists(allowedSlotsForFixedTime, room);
                }
            }, 0);
            return;
        }

        setSelectedRoom((prevRoom) => {
            if (prevRoom && prevRoom.id !== room.id) {
                // Different room - reset slots
                setSelectedSlots([slotTime]);
                setSelectedTherapists([]);
                // Fetch for new room and slot
                setTimeout(() => {
                    const newKey = getSelectionKey([slotTime], room);
                    if (newKey && newKey !== lastFetchedSlotsRef.current) {
                        fetchAvailableTherapists([slotTime], room);
                    }
                }, 0);
                return room;
            } else {
                // Same room - toggle slot
                setSelectedSlots((prevSlots) => {
                    const isSelected = prevSlots.some((s) => s.isSame(slotTime, "minute"));

                    let newSlots;
                    if (isSelected) {
                        newSlots = prevSlots.filter((s) => !s.isSame(slotTime, "minute"));
                    } else {
                        // Check duration limit - don't allow selecting more than required duration
                        const tempSlots = [...prevSlots, slotTime].sort((a, b) => a.diff(b));
                        const newDuration = tempSlots.length * slotDuration;
                        if (newDuration > treatmentDuration) {
                            // Don't add if it exceeds the required duration
                            newSlots = prevSlots;
                        } else {
                            newSlots = tempSlots;
                        }
                    }
                    
                    // Check if we need to fetch therapists after state update
                    setTimeout(() => {
                        if (newSlots.length > 0 && room) {
                            const newKey = getSelectionKey(newSlots, room);
                            if (newKey && newKey !== lastFetchedSlotsRef.current) {
                                fetchAvailableTherapists(newSlots, room);
                            }
                        } else {
                            setAvailableTherapists([]);
                            lastFetchedSlotsRef.current = null;
                            lastFetchedRoomIdRef.current = null;
                        }
                    }, 0);
                    
                    return newSlots;
                });
                return room;
            }
        });
    }, [gridDate, currentTime, getScheduleForSlot, slotDuration, treatmentDuration, getSelectionKey, fetchAvailableTherapists, fixedStartTime, allowedSlotsForFixedTime, isSlotAllowedForFixedTime, isSlotInBookingBuffer, currentSession, isSlotWithinSession]);

    // Handle drag selection - works with individual slots
    const handleMouseDown = useCallback((room, slotTime) => {
        const slotDateTime = gridDate.hour(slotTime.hour()).minute(slotTime.minute());
        if (slotDateTime.isBefore(currentTime)) {
            return;
        }

        const schedule = getScheduleForSlot(room, slotTime);
        if (schedule) {
            return; // Don't allow selection of occupied slots
        }

        if (fixedStartTime && !isSlotAllowedForFixedTime(slotTime)) return;
        if (isSlotInBookingBuffer(room, slotTime)) return; // 15-min buffer before/after booking

        if (currentSession && !isSlotWithinSession(slotTime)) return; // Only allow slots within session time range

        setIsDragging(true);
        setDragStart({ room, slotTime });
        setSelectedRoom(room);
        setSelectedSlots([slotTime]);
    }, [gridDate, currentTime, getScheduleForSlot, fixedStartTime, isSlotAllowedForFixedTime, isSlotInBookingBuffer, currentSession, isSlotWithinSession]);

    const handleMouseEnter = useCallback((room, slotTime) => {
        if (!isDragging || !dragStart) return;

        if (dragStart.room.id !== room.id) return;

        const slotDateTime = gridDate.hour(slotTime.hour()).minute(slotTime.minute());
        if (slotDateTime.isBefore(currentTime)) return;

        const schedule = getScheduleForSlot(room, slotTime);
        if (schedule) return;

        const startSlot = dragStart.slotTime;
        const endSlot = slotTime;
        const newSlots = [];

        const startIdx = timeSlots.findIndex((t) => t.isSame(startSlot, "minute"));
        const endIdx = timeSlots.findIndex((t) => t.isSame(endSlot, "minute"));

        if (startIdx === -1 || endIdx === -1) return;

        const minIdx = Math.min(startIdx, endIdx);
        const maxIdx = Math.max(startIdx, endIdx);

        for (let i = minIdx; i <= maxIdx; i++) {
            const slot = timeSlots[i];
            if (!slot) continue;
            const slotDateTime = gridDate.hour(slot.hour()).minute(slot.minute());
            if (!slotDateTime.isBefore(currentTime)) {
                const isOccupied = getScheduleForSlot(room, slot);
                if (!isOccupied) {
                    if (isSlotInBookingBuffer(room, slot)) {
                        // Skip 15-min buffer slots
                    } else if (currentSession && !isSlotWithinSession(slot)) {
                        // Skip slots outside session time range
                    } else if (fixedStartTime && allowedSlotsForFixedTime?.length) {
                        if (isSlotAllowedForFixedTime(slot)) newSlots.push(slot);
                    } else {
                        newSlots.push(slot);
                    }
                }
            }
        }

        // Restrict to duration limit - when fixed start time, use only allowed slots; else cap by treatmentDuration
        const maxSlots = fixedStartTime && allowedSlotsForFixedTime?.length
            ? allowedSlotsForFixedTime.length
            : Math.floor(treatmentDuration / slotDuration);
        const limitedSlots = newSlots.slice(0, maxSlots);

        // Only update if slots actually changed
        setSelectedSlots((prevSlots) => {
            const prevKey = prevSlots.map(s => s.format("HH:mm:ss")).sort().join(",");
            const newKey = limitedSlots.map(s => s.format("HH:mm:ss")).sort().join(",");
            if (prevKey === newKey) {
                return prevSlots; // Return same reference if content is the same
            }
            return limitedSlots;
        });
    }, [isDragging, dragStart, gridDate, currentTime, getScheduleForSlot, timeSlots, slotDuration, treatmentDuration, fixedStartTime, allowedSlotsForFixedTime, isSlotAllowedForFixedTime, isSlotInBookingBuffer, currentSession, isSlotWithinSession]);

    const handleMouseUp = useCallback(() => {
        const wasDragging = isDragging;
        setIsDragging(false);
        setDragStart(null);
        
        // When drag ends, check if selection changed and fetch therapists
        if (wasDragging) {
            // Use setTimeout to ensure state is updated
            setTimeout(() => {
                if (selectedSlots.length > 0 && selectedRoom) {
                    const currentKey = getSelectionKey(selectedSlots, selectedRoom);
                    // Only fetch if selection is different from last fetched
                    if (currentKey && currentKey !== lastFetchedSlotsRef.current) {
                        fetchAvailableTherapists(selectedSlots, selectedRoom);
                    }
                } else {
                    setAvailableTherapists([]);
                    lastFetchedSlotsRef.current = null;
                    lastFetchedRoomIdRef.current = null;
                }
            }, 0);
        }
    }, [isDragging, selectedSlots, selectedRoom, getSelectionKey, fetchAvailableTherapists]);

    // Get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "completed":
                return { bg: "#E0E0E0", border: "#888888", text: "#000" };
            case "scheduled":
                return { bg: "#E8F5E9", border: "#4CAF50", text: "#2E7D32" };
            case "ongoing":
                return { bg: "#E3F2FD", border: "#2196F3", text: "#1565C0" };
            case "canceled":
            case "cancelled":
                return { bg: "#FFEBEE", border: "#F44336", text: "#C62828" };
            default:
                return { bg: "#E8F5E9", border: "#4CAF50", text: "#2E7D32" };
        }
    };

    // Check if slot is selected
    const isSlotSelected = useCallback((slotTime) => {
        return selectedSlots.some((s) => s.isSame(slotTime, "minute")) && selectedRoom;
    }, [selectedSlots, selectedRoom]);

    // Calculate selected duration
    const selectedDuration = selectedSlots.length * slotDuration;
    const durationStatus = selectedDuration >= treatmentDuration ? "in" : "out";

    // Check if schedule therapy button should be enabled
    const canScheduleTherapy = useCallback(() => {
        if (!selectedRoom || selectedSlots.length === 0) return false;
        if (therapistCount) {
            const minCount = therapistCount;
            const maxCount = therapistCount * 2 || therapistCount;
            return (
                selectedTherapists.length >= minCount &&
                selectedTherapists.length <= maxCount
            );
        }
        return selectedTherapists.length > 0;
    }, [selectedRoom, selectedSlots.length, selectedTherapists.length, therapistCount]);

    // Handle schedule therapy - opens confirmation dialog
    const handleScheduleTherapy = useCallback(() => {
        if (!canScheduleTherapy()) return;
        setConfirmDialogOpen(true);
    }, [canScheduleTherapy]);

    // Actually submit the therapy schedule (called from dialog Save button)
    const submitScheduleTherapy = useCallback(() => {
        setConfirmDialogOpen(false);
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        const startTime = selectedSlots[0].format("HH:mm");
        const endTime = selectedSlots[selectedSlots.length - 1]
            .add(slotDuration, "minute")
            .format("HH:mm");   
        const dateStr = gridDate.format("YYYY-MM-DD");
        const payload = {
            session_id: initialSchedule?.id || null,
            treatment_date: dateStr,
            treatment_time: startTime,
            end_time: endTime,
            session: initialSchedule?.session || "",      
            treatment_ms_id: treatmentId.join(","),
            therapy_room_id: selectedRoom.id,
            therapists: selectedTherapists,
        };

        RestAPIService.create(
            payload,
            Serviceurls.AYURVEDA_SCHEDULING_TREATMENT
        )
            .then((res) => {
                if (res?.data?.status === "success") {
                    setSuccessMsg("Therapy scheduled successfully");
                    fetchSchedules();
                    setTimeout(() => {
                        onBack();
                    }, 2000);
                    setSelectedSlots([]);
                    setSelectedTherapists([]);
                    setSelectedRoom(null);
                    setConfirmDialogOpen(false);
                } else {
                    setErrorMsg(res?.data?.message || "Failed to schedule therapy");
                }
            })
            .catch((error) => {
                console.error("Error scheduling therapy:", error);
                setErrorMsg(error?.response?.data?.message || error?.message || "Failed to schedule therapy");
            })
            .finally(() => setLoading(false));
    }, [selectedSlots, slotDuration, gridDate, initialSchedule, selectedRoom, selectedTherapists, treatmentId, fetchSchedules, currentSession]);

    // Filter rooms
    const filteredRooms = rooms.filter((room) => {
        if (roomFilter && room.id !== roomFilter.id) return false;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                room.room_name?.toLowerCase().includes(query) ||
                room.room_number?.toLowerCase().includes(query)
            );
        }
        return true;
    });

    // Get current time position
    const getCurrentTimePosition = useCallback(() => {
        if (!gridDate.isSame(dayjs(), "day")) return null;
        const now = dayjs();
        const startOfDay = now.startOf("day").hour(6).minute(0);
        const diffMinutes = now.diff(startOfDay, "minute");
        if (diffMinutes < 0) return null;
        const slotIndex = Math.floor(diffMinutes / slotDuration);
        return slotIndex * 60; // 60px per slot
    }, [gridDate, slotDuration]);

    return (
        <Box sx={{ p: 2, bgcolor: Colors.Background, height: "calc(100vh - 100px)", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {onBack && (
                        <IconButton onClick={onBack} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" fontWeight={600}>
                        Scheduler
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {/* Room Filter */}
                    <Autocomplete
                        size="small"
                        options={rooms}
                        getOptionLabel={(option) => option.room_name || option.room_number || ""}
                        value={roomFilter}
                        onChange={(e, v) => {
                            setRoomFilter(v);
                            // Clear selections when filter changes if it doesn't match selected room
                            if (v && selectedRoom && selectedRoom.id !== v.id) {
                                setSelectedSlots([]);
                                setSelectedTherapists([]);
                                setSelectedRoom(null);
                            } else if (!v && selectedRoom) {
                                // Filter cleared, keep selections
                            }
                        }}
                        renderInput={(params) => <TextField {...params} label="Select Room" />}
                        sx={{ width: 150 }}
                    />
                    {/* <TextField
                        size="small"
                        placeholder="Search Room"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                        }}
                        sx={{ width: 200 }}
                    /> */}
                    {/* Date Navigation */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                const newDate = gridDate.subtract(1, "day");
                                setGridDate(newDate);
                                setSelectedSlots([]);
                                setSelectedTherapists([]);
                                setSelectedRoom(null);
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                            <DatePicker
                                value={gridDate}
                                onChange={(newValue) => {
                                    if (newValue != null) {
                                        const date = dayjs.isDayjs(newValue) ? newValue : dayjs(newValue);
                                        if (date.isValid()) {
                                            setGridDate(date);
                                            setSelectedSlots([]);
                                            setSelectedTherapists([]);
                                            setSelectedRoom(null);
                                        }
                                    }
                                }}
                                format="DD-MM-YYYY"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        sx={{ width: 150 }}                              
                                    />
                                )}
                            />
                        </LocalizationProvider>
                        <IconButton
                            size="small"
                            onClick={() => {
                                const newDate = gridDate.clone().add(1, "day");
                                setGridDate(newDate);
                                setSelectedSlots([]);
                                setSelectedTherapists([]);
                                setSelectedRoom(null);
                            }}
                        >
                            <ArrowForwardIcon />
                        </IconButton>
                        {/* Info Icon */}
                        {patientInfo && (
                            <Tooltip
                                title={
                                    <Box>
                                        <Typography variant="body2">
                                            <strong>Duration:</strong> {treatmentDuration} mins
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Patient:</strong> {patientInfo.patient_name || "-"}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Treatment:</strong> {therapyInfo?.treatment_name || therapyInfo?.therapy_name || "-"}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Instruction:</strong> {treatmentInstructions || "-"}
                                        </Typography>
                                    </Box>
                                }
                            >
                                <IconButton size="small">
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleScheduleTherapy}
                        disabled={!canScheduleTherapy()}
                        sx={{
                            backgroundColor: "#4da3d4",
                            "&:hover": { backgroundColor: "#3a8bb3" },
                            "&:disabled": { backgroundColor: "#EAEAEA", color: "#A9A9A9" },
                        }}
                    >
                        SCHEDULE THERAPY
                    </Button>
                </Box>
            </Box>

            {/* Duration Info - Always Show */}
            <Box sx={{ mb: 1, p: .5, bgcolor: "#fff", borderRadius: 1, flexShrink: 0 }}>
                <Typography variant="body2">
                    {selectedSlots.length > 0 ? (
                        <>
                            Selected Duration: <strong>{selectedDuration} mins</strong> / {treatmentDuration} mins{" "}
                            <Chip
                                label={durationStatus === "in" ? "In Duration" : "Out of Duration"}
                                size="small"
                                color={durationStatus === "in" ? "success" : "warning"}
                                sx={{ ml: 1 }}
                            />
                            {selectedDuration >= treatmentDuration && (
                                <Chip
                                    label="Duration Limit Reached"
                                    size="small"
                                    color="info"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            Required Duration: <strong>{treatmentDuration} mins</strong> | Select slots to schedule
                            {fixedStartTime && (
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    (Only {fixedStartTime.format("hh:mm A")} – {fixedStartTime.add(treatmentDuration, "minute").format("hh:mm A")} allowed)
                                </Typography>
                            )}
                            {currentSession && SESSION_TIME_RANGES[currentSession] && (
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    (Session: {currentSession} – {dayjs().hour(SESSION_TIME_RANGES[currentSession].start.hour).minute(0).format("h A")} to {dayjs().hour(SESSION_TIME_RANGES[currentSession].end.hour).minute(0).format("h A")})
                                </Typography>
                            )}
                        </>
                    )}
                </Typography>
            </Box>

            {/* Grid - Scrollable */}
            <Box
                ref={gridRef}
                sx={{
                    position: "relative",
                    overflow: "auto",
                    flex: 1,
                    border: "1px solid #e0e0e0",
                    bgcolor: "#fff",
                }}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    backgroundColor: Colors.TableHeaderBarBGColor,
                                    fontWeight: 600,
                                    minWidth: 150,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 3,
                                    borderLeft: "1px solid #e0e0e0",
                                    borderRight: "1px solid #e0e0e0",
                                }}
                            >
                                Rooms
                            </TableCell>
                            {timeSlots && timeSlots.length > 0 ? timeSlots.map((slot, idx) => {
                                const slotTime = slot.format("hh:mm A");
                                const [time, ampm] = slotTime.split(" ");
                                return (
                                    <TableCell
                                        key={idx}
                                        sx={{
                                            backgroundColor: Colors.TableHeaderBarBGColor,
                                            fontWeight: 600,
                                            minWidth: 60,
                                            textAlign: "center",
                                            p: 0.5,
                                            whiteSpace: "nowrap",
                                            borderLeft: "1px solid #e0e0e0",
                                            borderRight: "1px solid #e0e0e0",
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" component="span">
                                                {time}
                                            </Typography>
                                            {/* <Typography variant="caption" component="span" sx={{ ml: 0.5 }}>
                                                {ampm}
                                            </Typography> */}
                                        </Box>
                                    </TableCell>
                                );
                            }) : (
                                <TableCell colSpan={1} align="center" sx={{ borderLeft: "1px solid #e0e0e0", borderRight: "1px solid #e0e0e0" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No time slots available
                                    </Typography>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRooms.map((room) => (
                            <TableRow key={room.id} sx={{ height: 50 }}>
                                <TableCell
                                    sx={{
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 2,
                                        backgroundColor: "#fff",
                                        fontWeight: 600,
                                        height: 50,
                                    }}
                                >
                                    {room.room_name || room.room_number || `Room ${room.id}`}
                                </TableCell>
                                {timeSlots && timeSlots.length > 0 && (() => {
                                    const cells = [];
                                    let skipCount = 0;

                                    return timeSlots.map((slotTime, idx) => {
                                        // Skip cells that are part of a previous schedule's span
                                        if (skipCount > 0) {
                                            skipCount--;
                                            return null;
                                        }

                                        const schedule = getScheduleForSlot(room, slotTime);
                                        const isSelected = isSlotSelected(slotTime) && selectedRoom?.id === room.id;
                                        const slotDateTime = gridDate.hour(slotTime.hour()).minute(slotTime.minute());
                                        const isPast = slotDateTime.isBefore(currentTime);
                                        const isDisabledByFixedTime = fixedStartTime && !isSlotAllowedForFixedTime(slotTime);
                                        const isInBuffer = isSlotInBookingBuffer(room, slotTime);
                                        const isOutsideSession = currentSession && !isSlotWithinSession(slotTime);
                                        const statusColor = schedule ? getStatusColor(schedule.status) : null;
                                        const isStart = isScheduleStart(room, slotTime, schedule);
                                        const span = isStart ? getScheduleSpan(schedule) : 1;

                                        // If this is the start of a schedule, skip the next (span - 1) cells
                                        if (isStart && span > 1) {
                                            skipCount = span - 1;
                                        }

                                        return (
                                            <TableCell
                                                key={idx}
                                                colSpan={span > 1 ? span : 1}
                                                sx={{
                                                    p: 0,
                                                    position: "relative",
                                                    minWidth: 60,
                                                    height: 50,
                                                    borderRadius: schedule ? 2 : 0,
                                                    border: isSelected ? "none" : "1px solid #e0e0e0",
                                                    borderLeft: isSelected ? "none" : schedule
                                                        ? `4px solid ${statusColor.border}`
                                                        : "1px solid #e0e0e0",
                                                    backgroundColor: isSelected
                                                        ? "#E3F2FD"
                                                        : schedule
                                                            ? statusColor.bg
                                                            : "#fff",
                                                    borderColor: isSelected
                                                    ? "#2196F3" : schedule ? statusColor.border : "#e0e0e0",
                                                    cursor: isPast || schedule || isDisabledByFixedTime || isInBuffer || isOutsideSession ? "default" : "pointer",
                                                    opacity: isPast || isDisabledByFixedTime || isInBuffer || isOutsideSession ? 0.5 : 1,
                                                }}
                                                onMouseDown={() => !isDisabledByFixedTime && !isInBuffer && !isOutsideSession && handleMouseDown(room, slotTime)}
                                                onMouseEnter={() => handleMouseEnter(room, slotTime)}
                                                onClick={() => !isDragging && !isDisabledByFixedTime && !isInBuffer && !isOutsideSession && handleSlotClick(room, slotTime)}
                                            >
                                                {schedule && isStart && (
                                                    <Box
                                                        sx={{
                                                            p: 0.5,
                                                            fontSize: "0.7rem",
                                                            color: statusColor.text,
                                                            fontWeight: 600,
                                                            height: "100%",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Typography variant="caption" noWrap>
                                                            {schedule.patient_name || "-"}
                                                        </Typography>
                                                        <Typography variant="caption" noWrap>
                                                            {schedule.treatment_time || "-"} - {schedule.end_time || "-"}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        );
                                    });
                                })()}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {/* Current Time Line */}
                {getCurrentTimePosition() !== null && (
                    <Box
                        sx={{
                            position: "absolute",
                            left: 150 + getCurrentTimePosition(),
                            top: 0,
                            bottom: 0,
                            width: 2,
                            backgroundColor: "#4da3d4",
                            zIndex: 10,
                            pointerEvents: "none",
                        }}
                    />
                )}
            </Box>

            {/* Available Therapists - Fixed at Bottom with smooth transitions */}
            <Box
                sx={{
                    mt: .5,
                    p: selectedSlots.length > 0 ? .5 : 0,
                    bgcolor: "#fff",
                    borderRadius: 1,
                    flexShrink: 0,
                    maxHeight: selectedSlots.length > 0 ? "300px" : "0px",
                    overflow: "hidden",
                    transition: "all 0.3s ease-in-out",
                    opacity: selectedSlots.length > 0 ? 1 : 0,
                }}
            >
                {selectedSlots.length > 0 && (
                    <>
                        <Typography variant="button" fontWeight={500} mb={.2}>
                            Available Therapists
                        </Typography>
                        {therapistLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 1, height: 100 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    overflowX: "auto",
                                    padding: .2,
                                    transition: "opacity 0.2s ease-in-out",
                                    opacity: therapistLoading ? 0.5 : 1,
                                }}
                            >
                                {availableTherapists.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No therapists available for selected time
                                    </Typography>
                                ) : (
                                    availableTherapists.map((therapist, avatarIndex) => {
                                        // Use therapist_id as the unique identifier
                                        const therapistId = therapist.therapist_id || therapist.id;
                                        const avatarColors = getAvatarColors(therapist.therapist_name, avatarIndex);
                                        const isSelected = selectedTherapists.some((t) => {
                                            const tId = t.therapist_id || t.id;
                                            return tId === therapistId;
                                        });
                                        return (
                                            <Box
                                                key={therapistId}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedTherapists(
                                                            selectedTherapists.filter((t) => {
                                                                const tId = t.therapist_id || t.id;
                                                                return tId !== therapistId;
                                                            })
                                                        );
                                                    } else {
                                                        const maxCount = therapistCount;
                                                        if (selectedTherapists.length < maxCount) {
                                                            setSelectedTherapists([...selectedTherapists, therapist]);
                                                        }
                                                    }
                                                }}
                                                sx={{
                                                    p: 1,
                                                    border: `2px solid ${isSelected ? "#4da3d4" : "#e0e0e0"}`,
                                                    borderRadius: 2,
                                                    backgroundColor: isSelected ? "#E3F2FD" : "#fff",
                                                    cursor: "pointer",
                                                    minWidth: 90,
                                                    maxWidth: 100,
                                                    maxHeight: 200,
                                                    textAlign: "center",
                                                    transition: "all 0.2s ease-in-out",
                                                    transform: isSelected ? "scale(1)" : "scale(1)",
                                                    "&:hover": {
                                                        borderColor: "#4da3d4",
                                                        transform: "scale(1.05)",
                                                    },
                                                }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 50,
                                                        height: 50,
                                                        mx: "auto",
                                                        mb: 0.2,
                                                        transition: "transform 0.2s ease-in-out",
                                                        backgroundColor: !therapist.user_image ? undefined : avatarColors.bg,
                                                        color: !therapist.user_image ? undefined : avatarColors.color,
                                                    }}
                                                >
                                                    {!therapist.user_image ? (
                                                        <img src={therapist.user_image} alt={therapist.therapist_name || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                    ) : (
                                                        <span style={{ color: avatarColors.color, fontWeight: 600 }}>
                                                            {therapist.therapist_name?.charAt(0)?.toUpperCase() || "?"}
                                                        </span>
                                                    )}
                                                </Avatar>                                               
                                                {therapist.therapist_name && therapist.therapist_name.length > 10 ?
                                                    <Tooltip title={therapist.therapist_name}>
                                                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.75rem" }}>
                                                            {therapist.therapist_name.slice(0, 10) + "..."}
                                                        </Typography>
                                                    </Tooltip> :
                                                     <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.75rem" }}>
                                                         {therapist.therapist_name || "-"}
                                                    </Typography>}
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                                    {therapist.employee_code || `Grade ${therapist.grade || "-"}`}
                                                </Typography>
                                            </Box>
                        );
                                    })
                                )}
                    </Box>
                        )}
                {/* {therapistCount && (
                            <Typography variant="caption" color="text.secondary" mt={0.5} sx={{ fontSize: "0.7rem" }}>
                                Select {therapistCount} to {therapistCount * 2 || therapistCount} therapist(s)
                            </Typography>
                        )} */}
            </>
                )}
        </Box>

            { loading && <Loader /> }
            {
                errorMsg && (
                    <ToastMsg severity="error" msg={errorMsg} msgPop={() => setErrorMsg("")} />
                )
            }
            {
                successMsg && (
                    <ToastMsg severity="success" msg={successMsg} msgPop={() => setSuccessMsg("")} />
                )
            }

            {/* Confirmation Dialog - Confirm Therapy Schedule */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        color: "#424242",
                        textAlign: "center",
                        pt: 2,
                        pb: 1,
                        position: "relative",
                    }}
                >
                    Confirm Therapy Schedule
                    <IconButton
                        aria-label="close"
                        onClick={() => setConfirmDialogOpen(false)}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "#757575",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: 2, pb: 1 }}>
                    {/* Info banner - therapist availability time */}
                    {selectedSlots.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 1.5,
                                mb: 2,
                                bgcolor: "#e3f2fd",
                                borderRadius: 1,
                                border: "1px solid #90caf9",
                            }}
                        >
                            <InfoIcon sx={{ color: "#1565c0", fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: "#0d47a1", fontWeight: 500 }}>
                                These therapists are available from{" "}
                                {selectedSlots[0].format("hh:mm A")} -{" "}
                                {selectedSlots[selectedSlots.length - 1]
                                    .add(slotDuration, "minute")
                                    .format("hh:mm A")}
                            </Typography>
                        </Box>
                    )}

                    {/* PATIENT DETAILS */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: "#424242",
                            letterSpacing: "0.05em",
                            display: "block",
                            mb: 0.5,
                        }}
                    >
                        PATIENT DETAILS
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box
                        sx={{
                            p: 1.5,
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Avatar
                            src={patientInfo?.photo_url}
                            sx={{ width: 48, height: 48, bgcolor: "#bdbdbd" }}
                        >
                            {(patientInfo?.patient_name || "?")[0]}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} color="#424242" noWrap>
                                {patientInfo?.patient_name || "-"}
                                {(patientInfo?.patient_age || patientInfo?.age)
                                    ? ` ${String(patientInfo?.patient_age || patientInfo?.age).replace(/\s*y\s*$/i, "")}/y`
                                    : ""}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {[patientInfo?.patient_account_number || patientInfo?.uh_id, patientInfo?.patient_mobile_number || patientInfo?.mobile_no]
                                    .filter(Boolean)
                                    .join(" | ") || "-"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* THERAPY DETAILS */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: "#424242",
                            letterSpacing: "0.05em",
                            display: "block",
                            mb: 0.5,
                        }}
                    >
                        THERAPY DETAILS
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box
                        sx={{
                            p: 1.5,
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                            mb: 2,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" display="block">
                            Treatment Name
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.25 }}>
                            <Typography variant="body2" fontWeight={600} color="#424242">
                                {therapyInfo?.treatment_name || therapyInfo?.therapy_name || "-"}
                            </Typography>
                            <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                                label={`${treatmentDuration} MINS`}
                                size="small"
                                sx={{
                                    bgcolor: "#fff3e0",
                                    border: "1px solid #ffb74d",
                                    color: "#e65100",
                                    fontWeight: 600,
                                    "& .MuiChip-icon": { color: "#e65100" },
                                }}
                            />
                        </Box>
                        {treatmentInstructions && (
                            <>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Instructions
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="#424242"
                                    sx={{
                                        mt: 0.25,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        maxHeight: 60,
                                        overflow: "auto",
                                    }}
                                >
                                    {treatmentInstructions}
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* ROOM DETAIL */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: "#424242",
                            letterSpacing: "0.05em",
                            display: "block",
                            mb: 0.5,
                        }}
                    >
                        ROOM DETAIL
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box
                        sx={{
                            p: 1.5,
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                            mb: 2,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" display="block">
                            Room Details
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="#424242">
                            {selectedRoom?.room_code && selectedRoom?.room_name
                                ? `${selectedRoom.room_code}/${selectedRoom.room_name}`
                                : selectedRoom?.room_name || selectedRoom?.room_number || selectedRoom?.room_code || "-"}
                        </Typography>
                    </Box>

                    {/* THERAPIST DETAILS */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: "#424242",
                            letterSpacing: "0.05em",
                            display: "block",
                            mb: 0.5,
                        }}
                    >
                        THERAPIST DETAILS
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box
                        sx={{
                            p: 1.5,
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                        }}
                    >
                        {selectedTherapists.length > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {selectedTherapists.map((therapist, idx) => (
                                    <Box
                                        key={therapist.therapist_id ?? idx}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            px: 1.5,
                                            py: 1,
                                            bgcolor: "#fff",
                                            borderRadius: 3,
                                            border: "1px solid #e0e0e0",
                                            minWidth: 0,
                                        }}
                                    >
                                        <Avatar
                                            sx={{ width: 32, height: 32, bgcolor: "#4da3d4", fontSize: "0.875rem" }}
                                        >
                                            {(therapist.therapist_name || "?")[0]}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} color="#424242" noWrap>
                                                {therapist.therapist_name || "-"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {therapist.employee_code || therapist.grade ? `Grade ${therapist.grade || ""}` : "Therapist"}
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setSelectedTherapists((prev) =>
                                                    prev.filter((_, i) => i !== idx)
                                                )
                                            }
                                            sx={{
                                                bgcolor: "#eeeeee",
                                                "&:hover": { bgcolor: "#e0e0e0" },
                                                width: 24,
                                                height: 24,
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No therapists selected
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1, gap: 1, justifyContent: "flex-end" }}>
                    <Button
                        variant="outlined"
                        onClick={() => setConfirmDialogOpen(false)}
                        sx={{
                            minWidth: 120,
                            borderColor: "#9e9e9e",
                            color: "#424242",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitScheduleTherapy}
                        sx={{
                            backgroundColor: "#04B7B1",
                            "&:hover": { backgroundColor: "#039a95" },
                            minWidth: 160,
                        }}
                        disabled={loading || selectedTherapists.length === 0}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : "Schedule Therapy"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SchedulerView;
